// Post-sign-in return path (AUTH upgrade-1). Every sign-out saves the page the user was on; both
// sign-in methods (email/password on LoginPage, Google via /auth/google/done) send them back to
// it. A deep link opened while signed out is saved the same way by ProtectedRoute.
//
// sessionStorage, not the URL: it survives the full-page Google OAuth round-trip in the same tab,
// and it is per-tab, so two tabs signed out on two pages each return to their own page.
// The path is sanitised on save AND on read (in-app paths only, never an open redirect), bound to
// the user who was signed in (a different user gets the landing page, not someone else's result),
// read-once, and ignored after 24 h. Storage failures degrade silently to "no saved page".
const KEY = 'quralyst.returnTo';
const MAX_AGE_MS = 24 * 60 * 60_000;

interface Entry {
  path: string;
  userId: string | null;
  savedAt: number;
}

/** In-app path only: a single leading `/`, not under `/auth/`, and not the landing page itself. */
export function isSafeReturnPath(path: unknown): path is string {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return false;
  if (path.includes('\\')) return false; // `/\evil.com` is protocol-relative in some browsers
  const pathname = path.split(/[?#]/, 1)[0];
  if (pathname === '/' || pathname === '') return false;
  if (pathname === '/auth' || pathname.startsWith('/auth/')) return false;
  return true;
}

function read(): Entry | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const e = JSON.parse(raw) as Partial<Entry>;
    if (!isSafeReturnPath(e.path) || typeof e.savedAt !== 'number') return null;
    return {
      path: e.path,
      userId: typeof e.userId === 'string' ? e.userId : null,
      savedAt: e.savedAt,
    };
  } catch {
    return null;
  }
}

function clear(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Remember `path` as where to go after the next sign-in. `keepSamePath` leaves an existing entry
 * for the SAME path untouched: ProtectedRoute re-renders during every sign-out while the old page
 * is still the location, and must not replace the user-bound entry the sign-out just saved with
 * an anonymous one. A different path (a new deep link pasted after signing out) still replaces it.
 */
export function saveReturnTo(
  path: string,
  userId: string | null,
  opts: { keepSamePath?: boolean } = {},
): void {
  if (!isSafeReturnPath(path)) return;
  if (opts.keepSamePath && read()?.path === path) return;
  try {
    const entry: Entry = { path, userId, savedAt: Date.now() };
    window.sessionStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable — sign-in falls back to the landing page */
  }
}

/** Read-once: returns the saved path for `userId` (or an anonymous deep link) and always clears it. */
export function consumeReturnTo(userId: string | null | undefined): string | null {
  const e = read();
  clear();
  if (!e) return null;
  if (Date.now() - e.savedAt > MAX_AGE_MS) return null;
  if (e.userId !== null && e.userId !== userId) return null;
  return e.path;
}

/** `pathname + search + hash` of a router location. */
export function locationToPath(loc: { pathname: string; search?: string; hash?: string }): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`;
}
